import { useState, useEffect, useRef } from "react";
import axios from 'axios';
import postHelper from '../helpers/postHelper';
import configs from '../helpers/configs';
import {
  IconButton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import WhatshotIcon from "@mui/icons-material/Whatshot";

export default function CurrentOrdersList({data, setData, component}) {
  const [sortBy, setSortBy] = useState("diff");
  const [sortOrder, setSortOrder] = useState("asc");
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const { baseUrl } = configs;

  const handleSort = (key) => {
    const isAsc = sortBy === key && sortOrder === "asc";
    setSortBy(key);
    setSortOrder(isAsc ? "desc" : "asc");
  };

  const sortedData = [...data].sort((a, b) => {
    let aVal = a[sortBy];
    let bVal = b[sortBy];

    if (sortBy === "diff") {
      aVal = a.percentageDiff;
      bVal = b.percentageDiff;
    }

    if (sortBy === "product_id") {
      return sortOrder === "asc"
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }

    if (sortBy === "created_time") {
      return sortOrder === "asc"
        ? new Date(aVal) - new Date(bVal)
        : new Date(bVal) - new Date(aVal);
    }

    return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
  });

  const translateToTangibleWords = (age) => {
    return age === 0 ? "today" : age === 1 ? "yesterday" : `${age} days ago`;
  };

  const calculateDaysAgo = (date) => {
    const createdDate = new Date(date);
    const currentDate = new Date();
    return Math.floor((currentDate - createdDate) / (1000 * 60 * 60 * 24));
  };

  const determineSeverity = (diff, percentageDiff) => {
    let severity = 'okay';
    if (percentageDiff > 20) severity = 'fuckThisShit';
    else if (percentageDiff > 10) severity = 'owItHurts';
    else if (percentageDiff > 4) severity = 'pleaseStop';
    return <span className={severity}>{diff} ({percentageDiff}%)</span>;
  };

  const calculatePotentialGain = (order) => {
    const approxFee = 0.0025;
    const { base_size = 0, limit_price = 0 } = order?.order_configuration?.limit_limit_gtc || {};
    const totalValue = base_size * limit_price;
    const estimatedFee = totalValue * approxFee;
    const netGain = totalValue - estimatedFee;
  
    return netGain.toFixed(2);
  };

  const calculateCanelationActions = (percentageDiff, age) => {
    if (percentageDiff >= 20 || age >= 25) {
      return "burn";
    }
  };

  const displayCheck = (element) => {
    if (component === 'currentOrders') {
      return element;
    } else if (component === 'viewer') {
      return null;
    }
    return component;
  }

  const burnOrder = async (orderId, product_id, size) => {
    try {
      await postHelper(`${baseUrl}/burnorder`, { orderId, product_id, size})
      setSnackbar({ open: true, message: `Order id ${orderId} was successfully burned!`, severity: 'info' });
      setData((prevData) => prevData.filter((item) => item.order_id !== orderId));
    } catch (error) {
      console.error("Error burning order:", error);
      setSnackbar({ open: true, message: `There was an error attempting to burn order: ${orderId}!`, severity: 'warn' });
    }
  };

  const cancelOrder = async (orderId) => {
    try {
      await axios.post("http://localhost:5000/cancelorder", { orderId }, {
        headers: { "Content-Type": "application/json" },
      });
      setSnackbar({ open: true, message: `Order id ${orderId} was successfully canceled!`, severity: 'info' });
      setData((prevData) => prevData.filter((item) => item.order_id !== orderId));
    } catch (error) {
      console.error("Error canceling order:", error);
      setSnackbar({ open: true, message: `There was an error attempting to cancel order: ${orderId}!`, severity: 'warn' });
    }
  };

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sortDirection={sortBy === "product_id" ? sortOrder : false}>
            <TableSortLabel
              active={sortBy === "product_id"}
              direction={sortBy === "product_id" ? sortOrder : "asc"}
              onClick={() => handleSort("product_id")}
            >
              Product
            </TableSortLabel>
          </TableCell>

          {displayCheck(<TableCell sortDirection={sortBy === "created_time" ? sortOrder : false}>
            <TableSortLabel
              active={sortBy === "created_time"}
              direction={sortBy === "created_time" ? sortOrder : "asc"}
              onClick={() => handleSort("created_time")}
            >
              Created
            </TableSortLabel>
          </TableCell>)}

          <TableCell>Days Ago</TableCell>
          {displayCheck(<TableCell>Sell Price</TableCell>)}
          {displayCheck(<TableCell>Live Price</TableCell>)}

          <TableCell sortDirection={sortBy === "diff" ? sortOrder : false}>
            <TableSortLabel
              active={sortBy === "diff"}
              direction={sortBy === "diff" ? sortOrder : "asc"}
              onClick={() => handleSort("diff")}
            >
              Diff
            </TableSortLabel>
          </TableCell>

          <TableCell>Approx Gain</TableCell>
          {displayCheck(<TableCell align="center">Actions</TableCell>)}
        </TableRow>
      </TableHead>
      <TableBody>
      {sortedData.map((item, index) => (
        <TableRow className='rowOnHov' key={index}>
          <TableCell>{item.product_id.split('-')[0]}</TableCell>
          {displayCheck(<TableCell>{new Date(item.created_time).toLocaleString()}</TableCell>)}
          <TableCell>{translateToTangibleWords(calculateDaysAgo(item.created_time))}</TableCell>
          {displayCheck(<TableCell>${item.price}</TableCell>)}
          {displayCheck(<TableCell>${item.livePrice}</TableCell>)}
          <TableCell>
            {determineSeverity(item.diff, item.percentageDiff)}
            {item.diffChange === "up" && <span style={{ color: 'green' }}> ↑</span>}
            {item.diffChange === "down" && <span style={{ color: 'red' }}> ↓</span>}
          </TableCell>
          <TableCell>${calculatePotentialGain(item)}</TableCell>
          {displayCheck(<TableCell align="center">
            {calculateCanelationActions(item.percentageDiff, calculateDaysAgo(item.created_time)) === "burn" && (
              <IconButton color="error" onClick={() => burnOrder(item.order_id, item.product_id, item.outstanding_hold_amount, item)}>
                <WhatshotIcon />
              </IconButton>
            )}
            <IconButton color="error" onClick={() => cancelOrder(item.order_id)}>
              <CancelIcon />
            </IconButton>
          </TableCell>)}
        </TableRow>
      ))}
    </TableBody>
    <Snackbar
        open={snackbar.open}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        autoHideDuration={4000}
        message={snackbar.message}
      />
  </Table>
  )
}